import { apiFetch, ApiFetchError } from './auth.ts';

/**
 * Typed fetch wrapper.
 *
 * Thin shim around `apiFetch` from `./auth.ts`. That helper centralizes:
 *   - CT One ID session-cookie auth (`credentials: 'include'`)
 *   - error envelope mapping → `ApiFetchError`
 *   - URL base resolution against `VITE_API_BASE_URL`
 *
 * We re-export it under an `apiClient` namespace so callers read consistently
 * (`apiClient.get<T>('/api/foo')`) without having to know whether the body
 * goes through `json` vs `rawBody`.
 */

export { ApiFetchError };
/** @deprecated alias kept for components that already imported `ApiError`. */
export const ApiError = ApiFetchError;
export type ApiError = ApiFetchError;

type Query = Record<string, string | number | boolean | null | undefined> | undefined;

interface RequestOptions {
  query?: Query;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Public endpoint — omit the session cookie. */
  anonymous?: boolean;
}

interface BodyOptions<TBody> extends RequestOptions {
  body?: TBody;
}

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.append(k, String(v));
  }
  const qs = params.toString();
  if (!qs) return path;
  return path.includes('?') ? `${path}&${qs}` : `${path}?${qs}`;
}

/**
 * Demo fallback is dev-only: when a GET fails (API not running yet — the data
 * pipeline lands later), we serve a deterministic synthesized dataset so the
 * globe experience stays designable and testable. Gated hard on dev/env flag;
 * aborted requests never fall back (they're cancellations, not failures).
 */
const DEMO_ALLOWED = import.meta.env.DEV || import.meta.env.VITE_DEMO_DATA === '1';

async function call<T>(
  method: string,
  path: string,
  options: BodyOptions<unknown> = {},
): Promise<T> {
  const url = withQuery(path, options.query);
  try {
    return await apiFetch<T>(url, {
      method,
      headers: options.headers,
      signal: options.signal,
      anonymous: options.anonymous,
      json: options.body,
    });
  } catch (err) {
    if (DEMO_ALLOWED && method === 'GET' && !options.signal?.aborted) {
      const { resolveDemoResponse } = await import('./demo/demo-interceptor.ts');
      const demo = resolveDemoResponse(url);
      if (demo !== undefined) return demo as T;
    }
    throw err;
  }
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => call<T>('GET', path, options),
  post: <T, B = unknown>(path: string, options?: BodyOptions<B>) => call<T>('POST', path, options),
  put: <T, B = unknown>(path: string, options?: BodyOptions<B>) => call<T>('PUT', path, options),
  patch: <T, B = unknown>(path: string, options?: BodyOptions<B>) =>
    call<T>('PATCH', path, options),
  delete: <T>(path: string, options?: BodyOptions<unknown>) => call<T>('DELETE', path, options),
};

export type ApiClient = typeof apiClient;
